/* ⚠️ GENERATED from app/services/sendOutcome.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   services/sendOutcome.ts — did this message go, or did it not.

   ── 🟥 REPORTED ────────────────────────────────────────────────────────────
   "it finishes uploading and it gets to 99% and then changes to sending with
    loading indicators and I feel like it actually sent, the other person gets
    them, but for me eventually will show failed" … "I click the trash icon and
    exit the chat and come back I will see them again either showing sending or
    already sent".

   ── 🟥 THE APP HAS BEEN GUESSING ───────────────────────────────────────────
   Nothing ever asked the server. A failure was inferred from two things, and
   both of them are wrong about a slow network in the same direction:

     1. A CAUGHT EXCEPTION. Every media send wrapped FOUR steps in one `try` —
        upload the bytes, write the message document, relay it over the socket,
        and then update the chat-LIST row (preview text, unread count). The
        fourth is cosmetic and reconciles on its own; the sender's own text
        path says so in a comment and backgrounds it. The media paths did not,
        so a `getDoc` that hung on the chat document — a read with no deadline —
        marked a message failed that the recipient was already looking at.

     2. A CLOCK. The watchdog gave media ninety seconds from the message's
        timestamp, and its "bytes are moving, be patient" grace ran only while
        progress was under 100. So the upload spent the entire budget and the
        delivery that follows it — which has no progress of its own to show —
        inherited whatever was left. On a bad link that is nothing. Hence 99%,
        then "sending", then a red !.

   🟢 SO A FAILURE NOW NEEDS EVIDENCE. The message document either exists on the
      server or it does not, and that question has an answer we can go and get.
      Three outcomes, not two: it is there (confirmed), it is provably not there
      (failed), or we could not ask (keep waiting). The third is the one the old
      code did not have, and every false red ! lived in it.

   🟥 "WE COULD NOT ASK" IS NEVER "IT FAILED". A clock that spins for a while on
      a bad link is a small annoyance; being told a photo failed when the other
      person is replying to it is the bug being fixed. When in doubt the app
      waits, and the tick arrives by itself the moment the server confirms.

   Pure on purpose — no Firestore, no timers. services/sendVerify.ts does the
   asking; this file decides what the answer means, for both clients.
   ═══════════════════════════════════════════════════════════════════════════ */

/** The message types that upload bytes before they can be delivered. */
export const MEDIA_TYPES = ['image', 'video', 'images', 'audio', 'document'];

/* ── the stages of a send, and which of them can fail it ────────────────────
   🟥 THE LIST IS THE FIX. `bookkeeping` is in it so that it can be named as
      the stage a failure must NOT be attributed to. */

/**
 * Did this stage happen before or after the message reached the server?
 *
 * 🟥 ONCE THE DOCUMENT IS WRITTEN THE SEND IS OVER. Everything after it —
 *    relaying a push, bumping the chat-list preview, incrementing an unread
 *    count — is bookkeeping about a message that has already been sent, and a
 *    throw there is a bookkeeping failure wearing a send failure's clothes.
 */
export function isCosmeticStage(stage) {
  return stage === 'relaying' || stage === 'bookkeeping';
}

/* ── what "the server has it" means ─────────────────────────────────────────
   🟥 ONE DEFINITION, because there were three. SendWatchdog knew `seq` and
      `sent`; sendState also accepted `uploadStatus === 'success'`; the seq-ack
      handler cleared `sendFailed` and left `uploadStatus` alone, so a media
      message that healed still wore the red !. */
export function isSendConfirmed(row) {
  if (!row) return false;
  return row.seq != null || row.sent === true || row.uploadStatus === 'success';
}

/** Deliberately over: cancelled and trashed sends are settled, not pending. */
export function isSendSettled(row) {
  if (!row) return false;
  return !!row._uploadCancelled || row.uploadStatus === 'paused' || row.uploadStatus === 'retry_available';
}

/**
 * The patch that un-fails a row when a confirmation finally lands.
 *
 * 🟥 THE OLD ONE CLEARED `sendFailed` AND NOTHING ELSE, and media is failed
 *    through `uploadStatus`. So the self-healing the watchdog's header promises
 *    — "when it lands, the normal confirmation path clears the flag" — worked
 *    for text and never once for a photo. The red ! was permanent on exactly
 *    the messages this bug produces.
 *
 * 🟢 A CANCELLED ROW IS NOT HEALED. The person meant that; see the guard in
 *    `mayApplyConfirmation`.
 */
export function confirmPatch(row) {
  const isMedia = MEDIA_TYPES.includes(String(row?.type || ''));
  const patch = {
    isSending: false,
    sent: true,
    sendFailed: false,
    error: false
  };
  if (isMedia) {
    patch.uploadStatus = 'success';
    patch.uploadProgress = 100;
  }
  return patch;
}

/**
 * May a confirmation be written to this row at all?
 *
 * 🟥 THIS IS WHY A TRASHED MESSAGE CAME BACK. Every other write path in
 *    ChatModal checks the cancelled-id set before touching disk; the seq-ack
 *    handler did not, so an ack that arrived after the bin was pressed wrote
 *    the row straight back to SQLite. Leave the chat, come back, there it is —
 *    "either showing sending or already sent", exactly as reported.
 */
export function mayApplyConfirmation(row, cancelledIds) {
  if (!row) return false;
  if (!cancelledIds || typeof cancelledIds.has !== 'function') return true;
  /* 🟥 BOTH IDS. A media row is trashed under whichever id it happened to be
     wearing — the temp one before the document write, the real one after — and
     checking only one of them is how half of these came back. */
  for (const k of [row.id, row.clientTempId, row.serverId]) {
    if (k && cancelledIds.has(String(k))) return false;
  }
  return true;
}

/* ── the clock, when there is one ───────────────────────────────────────────
   🟥 THE UPLOAD MUST NOT BE CHARGED TO THE DELIVERY. These are two phases with
      two different shapes: one moves bytes and can report a percentage, the
      other is a single round trip that reports nothing at all. Measuring both
      against one deadline that starts when the user pressed send means a big
      album on a slow link arrives at the delivery phase with its budget already
      spent — which is the reported bug, to the second. */
export const UPLOAD_STALL_MS = 90000; // no byte movement at all
export const DELIVER_MS = 45000; // the document write, from the moment the bytes are up
export const DEAD_NET_MS = 12000; // the link is provably unusable

/**
 * Has this send been pending long enough to be worth CHECKING?
 *
 * 🟢 NOTE WHAT THIS DOES NOT DO. It never says "failed" — it says "stop
 *    waiting and go ask the server". The answer decides, not the clock.
 */
export function shouldVerify(row, c) {
  if (!row || isSendConfirmed(row) || isSendSettled(row)) return false;
  const dead = c.reachable === false;
  const isMedia = MEDIA_TYPES.includes(String(row.type || ''));
  if (!isMedia) return c.now - c.startedAt >= (dead ? DEAD_NET_MS : DELIVER_MS);
  const pct = Number(c.pct);
  const uploading = !c.deliverAt && Number.isFinite(pct) && pct > 0 && pct < 100;
  if (uploading) return c.now - c.startedAt >= (dead ? DEAD_NET_MS : UPLOAD_STALL_MS);

  /* 🟢 THE DELIVERY PHASE OWNS ITS OWN CLOCK. `deliverAt` is stamped when the
     last byte lands; with none — an app restarted mid-send — fall back to the
     start, because a send we cannot date is exactly one worth asking about. */
  const from = c.deliverAt || c.startedAt;
  return c.now - from >= (dead ? DEAD_NET_MS : DELIVER_MS);
}

/* ── the verdict ───────────────────────────────────────────────────────────── */

/** What the server said when we asked whether the message document is there. */

// no evidence either way — keep the clock

/**
 * 🟥 THE WHOLE POINT OF THE FILE IS THE THIRD BRANCH. `unknown` used to be
 *    spelled `failed`, because a timer ran out and nobody had anything better
 *    to say. Now it is spelled `wait`, and the message keeps its clock until
 *    something real happens to it.
 *
 * 🟥 AND A ROW WITH NO SERVER ID CANNOT BE ASKED ABOUT. Never having reached
 *    the point of writing a document is not proof of failure either — the write
 *    may be sitting in Firestore's own queue, which resolves on reconnect. It
 *    earns a red ! only from a link we know is dead.
 */
export function sendVerdict(row, answer) {
  if (isSendConfirmed(row)) return {
    kind: 'confirmed'
  };
  if (answer === 'exists') return {
    kind: 'confirmed'
  };
  if (answer === 'missing') return {
    kind: 'failed'
  };
  /* 🟥 AND THERE IS NO CLEVER FOURTH CASE. Being offline is not evidence: the
     write may be sitting in Firestore's own queue, which resolves on reconnect
     and confirms itself. The chat LIST still draws a ! for my own unconfirmed
     message while the link is down — see sendState.rowTickFailed, which is a
     display rule that corrects itself — but nothing durable is written here. */
  return {
    kind: 'wait'
  };
}

/* ── a send whose app died under it ─────────────────────────────────────────
   🟥 A DOCUMENT, A VOICE MEMO OR AN ALBUM KILLED MID-UPLOAD SPUN FOR EVER. Their rows
      carry only a `temp-` id until the document is written, so there is nothing to ask
      the server about (serverIdOf → null → "wait"), and — unlike a single photo or
      video — they have no durable outbox to resume them. The ring turned forever.
   🟢 BUT ONE THING IS CERTAIN: an upload is JavaScript in THIS process. A temp row that
      was created BEFORE this process started and has no live upload entry cannot be
      uploading — whatever was sending it died with the old process. That row has failed,
      and earns the red ! and Retry (which asks the server first, so a send that did land
      is reconciled, never duplicated). Nothing here guesses from a clock: a slow upload
      started in THIS process is never touched. */
export const ORPHANABLE_TYPES = ['document', 'audio', 'images'];
export function isOrphanedSend(row, startedAtMs, processStartedAtMs, hasLiveUpload) {
  if (!row || hasLiveUpload) return false;
  if (!ORPHANABLE_TYPES.includes(String(row.type || ''))) return false;
  if (isSendConfirmed(row) || isSendSettled(row)) return false;
  if (row.uploadStatus === 'failed' || row.sendFailed === true) return false;
  if (serverIdOf(row) !== null) return false; // a real id — the server can be asked instead
  if (!(row.isSending === true || row.sent === false)) return false;
  return Number.isFinite(startedAtMs) && startedAtMs > 0 && startedAtMs < processStartedAtMs;
}

/** A pre-allocated Firestore id is a real one; a `temp-…` placeholder is not. */
export function serverIdOf(row) {
  const id = String(row?.serverId || row?.id || '');
  if (!id || id.startsWith('temp-')) return null;
  return id;
}
export function hasNoServerId(row) {
  return serverIdOf(row) === null;
}

/* ── the failure patch, for when one is genuinely earned ───────────────────── */
export function failPatch(row) {
  const isMedia = MEDIA_TYPES.includes(String(row?.type || ''));
  return isMedia ? {
    isSending: false,
    sent: false,
    uploadStatus: 'failed'
  } : {
    isSending: false,
    sent: false,
    sendFailed: true
  };
}

/** ⏫ A file of mine still going up (photo, video, voice note, document, album): a hold on it opens no menu —
    there is nothing to react to, copy or forward yet; its ✕ and its retry are on the bubble. */
export function isUploadingMedia(m) {
  if (!m) return false;
  const t = m.type;
  if (t !== 'image' && t !== 'video' && t !== 'audio' && t !== 'document' && t !== 'images') return false;
  if (m.isSticker || m.isGif) return false;
  if (m.uploadStatus === 'failed' || m._uploadCancelled) return false; // a failed one has its own menu (retry / delete)
  return m.isSending === true || m.uploadStatus === 'uploading' || m.uploadStatus === 'paused' || m.uploadStatus === 'retry_available';
}
